"use client";

import Image from "next/image";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { CLOUDFLARE_STREAM_VIDEOS } from "@/config/cloudflare-media";
import { DeferredDecorativeVideo } from "./deferred-decorative-video";

export type BlueprintFeatureCardData = {
	num: string;
	label: string;
	highlight: string;
	title: string;
	desc: string;
	icon?: ReactNode;
	stats: { label: string; value: string }[];
	video?: string;
	images?: string[];
};

function BlueprintCorner({ className = "" }: { className?: string }) {
	return (
		<svg
			aria-hidden
			viewBox="0 0 16 16"
			className={`text-primary/45 group-hover:opacity-0 absolute h-4 w-4 transition-opacity duration-500 ${className}`}
			fill="none"
			stroke="currentColor"
			strokeWidth="1"
		>
			<path d="M0 6 V0 H6" />
			<circle cx="0" cy="0" r="1" fill="currentColor" />
		</svg>
	);
}

function BlueprintFeatureCard({
	num,
	label,
	highlight,
	title,
	desc,
	icon,
	stats,
	video,
	images,
}: BlueprintFeatureCardData) {
	const streamVideo = video ? CLOUDFLARE_STREAM_VIDEOS[video] : undefined;
	const previewImage = images?.[0];
	const [active, setActive] = useState(false);
	const [canPlayVideo, setCanPlayVideo] = useState(false);
	const cardRef = useRef<HTMLDivElement>(null);
	const [inViewport, setInViewport] = useState(false);

	useEffect(() => {
		const media = window.matchMedia?.("(hover: hover) and (pointer: fine)");
		if (!media) return;
		const update = () => setCanPlayVideo(media.matches);
		update();
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, []);

	useEffect(() => {
		if (!canPlayVideo || !cardRef.current || !window.IntersectionObserver) return;
		const observer = new IntersectionObserver(([entry]) => setInViewport(entry?.isIntersecting ?? false), {
			rootMargin: "120px 0px",
		});
		observer.observe(cardRef.current);
		return () => observer.disconnect();
	}, [canPlayVideo]);

	return (
		<div
			ref={cardRef}
			className="feature-card hover:border-primary/40 group relative min-h-[88px] min-w-0 cursor-default overflow-hidden rounded-xl border border-border bg-primary-soft transition-colors sm:min-h-[110px] sm:rounded-2xl lg:min-h-[420px]"
			tabIndex={streamVideo ? 0 : undefined}
			onPointerEnter={() => canPlayVideo && setActive(true)}
			onPointerLeave={() => setActive(false)}
			onFocus={() => canPlayVideo && setActive(true)}
			onBlur={() => setActive(false)}
		>
			<div
				aria-hidden
				className="absolute inset-0 bg-gradient-to-br from-primary-soft via-card to-primary-soft"
			/>
			<div
				aria-hidden
				className="absolute inset-0 opacity-[0.18]"
				style={{
					backgroundImage:
						"linear-gradient(to right, var(--primary) 1px, transparent 1px), linear-gradient(to bottom, var(--primary) 1px, transparent 1px)",
					backgroundSize: "24px 24px",
				}}
			/>
			<BlueprintCorner className="left-3 top-3" />
			<BlueprintCorner className="right-3 top-3 rotate-90" />
			<BlueprintCorner className="bottom-3 left-3 -rotate-90" />
			<BlueprintCorner className="bottom-3 right-3 rotate-180" />

			{/* Mobile: Bild links als Vorschau. Desktop: Icon oben-rechts (wie vorher) */}
			{(previewImage || streamVideo) && (
				<div
					aria-hidden
					className="group-hover:opacity-0 pointer-events-none absolute left-3 top-1/2 h-[70%] w-[68px] -translate-y-1/2 overflow-hidden rounded-md transition-opacity duration-500 sm:left-3.5 sm:w-[80px] lg:hidden"
				>
					{previewImage ? (
						<Image src={previewImage} alt="" fill className="object-cover" sizes="80px" />
					) : streamVideo ? (
						// Stream posters are runtime URLs, so native img avoids an optimizer allow-list.
						// eslint-disable-next-line @next/next/no-img-element
						<img className="h-full w-full object-cover" src={streamVideo.poster} loading="lazy" alt="" />
					) : null}
				</div>
			)}
			{icon && (
				<div
					aria-hidden
					className="group-hover:opacity-0 pointer-events-none absolute hidden text-primary opacity-90 transition-opacity duration-500 lg:right-4 lg:top-8 lg:block lg:[&>svg]:h-12 lg:[&>svg]:w-12"
				>
					{icon}
				</div>
			)}

			{streamVideo && (
				<div
					aria-hidden
					className="group-focus:opacity-100 group-hover:opacity-100 absolute inset-0 opacity-0 transition-opacity duration-700 ease-out"
				>
					<DeferredDecorativeVideo
						src={streamVideo.src}
						poster={streamVideo.poster}
						active={active && canPlayVideo && inViewport}
					/>
					<div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />
				</div>
			)}

			<div className="relative flex h-full min-w-0 flex-col justify-center py-3 pl-[88px] pr-4 sm:py-3.5 sm:pl-[100px] lg:justify-start lg:p-4 2xl:p-6">
				<div className="text-primary/70 group-focus:text-white/85 group-hover:text-white/85 font-mono text-[0.52rem] uppercase tracking-[0.18em] transition-colors duration-500 sm:text-[0.56rem] lg:min-h-20 lg:shrink-0 lg:text-[0.52rem] lg:tracking-[0.14em] 2xl:text-[0.6rem] 2xl:tracking-[0.22em]">
					{num}
					<span className="text-primary/30 mx-1.5 lg:hidden">·</span>
					<span className="text-primary/70 lg:hidden">{label}</span>
				</div>
				<div className="mt-0.5 lg:mt-auto">
					<div className="text-primary/70 group-focus:text-white/85 group-hover:text-white/85 hidden font-mono text-[0.52rem] uppercase tracking-[0.14em] transition-colors duration-500 lg:block 2xl:text-[0.58rem] 2xl:tracking-[0.2em]">
						{label}
					</div>
					<div className="group-focus:text-white group-hover:text-white hyphens-auto break-words font-display text-[0.95rem] font-medium leading-[1.15] tracking-[-0.014em] text-primary-dark transition-colors duration-500 sm:text-[1.05rem] lg:mt-1.5 lg:text-[clamp(0.9rem,1.2vw,1.25rem)] lg:leading-[1.05] lg:tracking-[-0.018em] 2xl:text-[1.5rem]">
						{highlight}
					</div>
				</div>
				{/* Detail-Block: nur Desktop hover-reveal — auf Mobile gar nicht rendern für Kompaktheit */}
				<div className="mt-5 hidden max-w-full overflow-hidden lg:block">
					<div className="group-focus:translate-y-0 group-focus:opacity-100 group-hover:translate-y-0 group-hover:opacity-100 translate-y-2 opacity-0 transition-all delay-200 duration-500 ease-out">
						<div className="mb-3 h-px w-12 bg-white/80" />
						<div className="font-display text-[1.05rem] font-medium leading-snug tracking-[-0.01em] text-white">
							{title}
						</div>
						<p className="mt-2 max-w-[32ch] text-[0.85rem] leading-snug text-white/85">{desc}</p>
						{stats.length > 0 && (
							<div className="mt-4 grid max-w-[32ch] grid-cols-3 gap-3">
								{stats.map((s) => (
									<div key={s.label}>
										<div className="font-mono text-[0.55rem] uppercase tracking-[0.16em] text-white/70">
											{s.label}
										</div>
										<div className="mt-0.5 whitespace-nowrap font-mono text-[0.85rem] font-medium text-white">
											{s.value}
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

interface BlueprintFeatureCardsProps {
	cards: BlueprintFeatureCardData[];
}

export function BlueprintFeatureCards({ cards }: BlueprintFeatureCardsProps) {
	return (
		<div
			className="relative flex min-w-0 flex-col gap-2.5 sm:gap-3 lg:h-auto lg:flex-row lg:gap-3 lg:[&>.feature-card:hover]:grow-[2.6] lg:[&>.feature-card]:min-w-0 lg:[&>.feature-card]:grow lg:[&>.feature-card]:basis-0 lg:[&>.feature-card]:transition-[flex-grow] lg:[&>.feature-card]:duration-700 lg:[&>.feature-card]:ease-out-expo"
			style={{
				marginRight: "clamp(-200px, calc(40rem - 50vw - 2.5rem), 0px)",
				width: "calc(100% + clamp(0px, calc(50vw - 40rem + 2.5rem), 200px))",
			}}
		>
			{cards.map((card) => (
				<BlueprintFeatureCard key={card.num} {...card} />
			))}
		</div>
	);
}
